/* Diamond Feed — Table view (stateless render helpers).
   The orchestrator in diamond-feed-grid.js owns all state and calls
   DiamondFeedTableView.render(tbody, list, ctx). Sort + paging live there. */
window.DiamondFeedTableView = (function () {
  'use strict';
  var DF = window.DiamondFeed;
  var dash = function (v) { return (v == null || v === '') ? '—' : v; };

  // Inline expanded detail for the open row.
  function expHTML(s) {
    var name = DF.shapeLabel(s);
    // Certificate shows the grading lab (GIA/IGI) — as a link that opens the
    // report when cert numbers are shown, plain text otherwise. showCert=false
    // suppresses the link so the exact stone can't be looked up (the report
    // carries its number). The primary 4Cs live in the table columns + title,
    // so the spec grid only carries the secondary specs.
    var pdf = s.media && s.media.pdf;
    var certCell = (DF.config && DF.config.showCert === false)
      ? dash(s.lab)
      : (pdf
        ? '<a class="spec-link" href="' + pdf + '" target="_blank" rel="noopener">' + dash(s.lab) + '</a>'
        : dash(s.lab));
    var specs = [
      ['Polish', dash(s.polish)], ['Symmetry', dash(s.symmetry)],
      ['Measurements', s.meas ? s.meas + ' mm' : '—'],
      ['L/W Ratio', s.ratio != null ? s.ratio.toFixed(2) : '—'],
      ['Depth', s.depth != null ? s.depth + '%' : '—'],
      ['Table', s.table != null ? s.table + '%' : '—'],
      ['Fluorescence', dash(s.fluor)],
      ['Certificate', certCell]
    ].map(function (r) { return '<div class="t-exp__spec"><dt>' + r[0] + '</dt><dd>' + r[1] + '</dd></div>'; }).join('');

    // Auto-rotate the 360° on mobile (loupe360 needs the autoplay flag); desktop
    // keeps the static first frame until the shopper interacts.
    var autoplay = !!(window.matchMedia && window.matchMedia('(max-width: 680px)').matches);
    var mediaInner = DF.mediaHTML(s, 'video', { autoplay: autoplay });   // 360° spin (or image)

    return '<div class="t-exp">' +
        '<div class="t-exp__media">' + mediaInner + '</div>' +
        '<div class="t-exp__head">' +
          '<p class="t-exp__eyebrow">' + (s.type === 'lab' ? 'Lab-Grown' : 'Natural') + ' Diamond</p>' +
          '<h3 class="t-exp__title serif">' + DF.fmtCt(s.carat) + ' ct ' + name + '</h3>' +
        '</div>' +
        '<div class="t-exp__specs-wrap">' +
          '<button type="button" class="t-exp__specs-toggle" aria-expanded="false"><span>Specifications</span><span class="t-exp__specs-chev" aria-hidden="true"></span></button>' +
          '<dl class="t-exp__specs">' + specs + '</dl>' +
        '</div>' +
        '<div class="t-exp__buy">' +
          '<div class="t-exp__price">' +
            '<span class="t-exp__price-k">Total price</span>' +
            '<span class="t-exp__price-v">' + DF.fmtUSD(s.price) + '</span>' +
          '</div>' +
          '<div class="t-exp__actions">' +
            '<button type="button" class="t-exp__add">' + DF.addCTALabel() + '</button>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function detailRow(s, ctx) {
    var tr = document.createElement('tr');
    tr.className = 't-detail';
    tr.innerHTML = '<td colspan="7" class="t-detail__cell">' + expHTML(s) + '</td>';
    tr.querySelector('.t-exp__add').addEventListener('click', function (e) {
      e.stopPropagation(); ctx.onAdd(s, e.currentTarget);
    });
    var specsToggle = tr.querySelector('.t-exp__specs-toggle');
    var specsWrap = tr.querySelector('.t-exp__specs-wrap');
    specsToggle.addEventListener('click', function (e) {
      e.stopPropagation();
      var open = specsWrap.classList.toggle('is-open');
      specsToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    // Scale the 360° viewer to the compact expanded-media box (it renders at a
    // fixed native px size and would otherwise clip to its top-left corner).
    if (DF.fit360) requestAnimationFrame(function () { DF.fit360(tr); });
    return tr;
  }

  function row(s, openId, ctx) {
    var tr = document.createElement('tr');
    tr.className = 't-row';
    if (s.id === openId) tr.classList.add('is-open');
    tr.innerHTML =
      '<td data-label="Shape"><span class="t-shape"><span class="t-shape__glyph" style="--shape-icon:url(' + DF.shapeIcon(s.shape) + ')"></span>' +
        '<span class="t-shape__name">' + DF.shapeLabel(s) + '</span></span></td>' +
      '<td data-label="Carat"><span class="t-carat">' + DF.fmtCt(s.carat) + '</span></td>' +
      '<td data-label="Cut">' + DF.cutLabel(s) + '</td>' +
      '<td data-label="Color">' + dash(s.color) + '</td>' +
      '<td data-label="Clarity">' + dash(s.clarity) + '</td>' +
      '<td data-label="Price"><span class="t-price">' + DF.fmtUSD(s.price) + '</span></td>' +
      '<td class="t-cta"><span class="t-cta__chev" aria-hidden="true"></span></td>';
    tr.addEventListener('click', function () { ctx.onToggle(s.id === openId ? null : s.id); });
    return tr;
  }

  function render(tbody, list, ctx) {
    tbody.innerHTML = '';
    var frag = document.createDocumentFragment();
    list.forEach(function (s) {
      frag.appendChild(row(s, ctx.openId, ctx));
      if (s.id === ctx.openId) frag.appendChild(detailRow(s, ctx));
    });
    tbody.appendChild(frag);
  }

  // Map a canonical sort token (e.g. 'price-asc') → {field, asc} for header paint.
  function tokenToCol(token) {
    if (!token || token === 'featured') return { field: null, asc: false };
    var m = token.match(/^(price|carat|color|clarity|cut)-?(asc|desc)?$/);
    if (!m) return { field: null, asc: false };
    return { field: m[1], asc: m[2] === 'asc' };
  }

  function paintSort(theadRow, token) {
    var col = tokenToCol(token);
    theadRow.querySelectorAll('th').forEach(function (th) {
      var f = th.getAttribute('data-sort');
      var on = f && f === col.field;
      th.classList.toggle('is-sorted', !!on);
      th.classList.toggle('is-asc', !!on && col.asc);
      th.classList.toggle('is-desc', !!on && !col.asc);
    });
  }

  return { render: render, paintSort: paintSort };
})();
